import { gql, useQuery } from '@apollo/client'
import _ from 'lodash'
import { useMemo } from 'react'

import { useGroupFilter } from '../../../groupFilter'
import { useVariables } from '../../../variables'

type IDataType = Record<
  | 'listLocationGroupMetricValues'
  | 'priorListLocationGroupMetricValues'
  | 'yoyListLocationGroupMetricValues',
  {
    nodes: {
      metricSummaryData: Record<
        string,
        {
          name: string
          unit: 'CENT'
          value: number
        }
      >
    }[]
  }
>

const query = gql`
  query listLocationGroupMetricValues(
    $iCurrentStartDate: Date!
    $iCurrentEndDate: Date!
    $iPriorStartDate: Date!
    $iPriorEndDate: Date!
    $iLocationGroupIds: [Int!]!
    $iIntersectedLocationGroupIds: [Int!]
    $iMetricCodes: [String!]!
  ) {
    listLocationGroupMetricValues(
      iStartDate: $iCurrentStartDate
      iEndDate: $iCurrentEndDate
      iFilter: {
        location_group_ids: $iLocationGroupIds
        intersected_location_group_ids: $iIntersectedLocationGroupIds
        metrics: $iMetricCodes
      }
    ) {
      nodes {
        metricSummaryData
      }
    }

    priorListLocationGroupMetricValues: listLocationGroupMetricValues(
      iStartDate: $iPriorStartDate
      iEndDate: $iPriorEndDate
      iFilter: {
        location_group_ids: $iLocationGroupIds
        intersected_location_group_ids: $iIntersectedLocationGroupIds
        metrics: $iMetricCodes
      }
    ) {
      nodes {
        metricSummaryData
      }
    }

    yoyListLocationGroupMetricValues: listLocationGroupMetricValues(
      iStartDate: $iCurrentStartDate
      iEndDate: $iCurrentEndDate
      iFilter: {
        location_group_ids: $iLocationGroupIds
        intersected_location_group_ids: $iIntersectedLocationGroupIds
        metrics: $iMetricCodes
        use_yoy: true
      }
    ) {
      nodes {
        metricSummaryData
      }
    }
  }
`

const useFetchGroupMetricValue = (metrics: string[]) => {
  const { variables } = useVariables()

  const currentPeriod = variables.date?.getInfo(0)
  const priorPeriod = variables.date?.getInfo(-1)
  const { groupFilter } = useGroupFilter()
  const { data, loading } = useQuery<IDataType>(query, {
    variables: {
      iCurrentStartDate: currentPeriod?.dateRange.startDateStr,
      iCurrentEndDate: currentPeriod?.dateRange.endDateStr,
      iPriorStartDate: priorPeriod?.dateRange.startDateStr,
      iPriorEndDate: priorPeriod?.dateRange.endDateStr,
      iLocationGroupIds:
        variables.allStores?.locationGroupIds || groupFilter?.ids,
      iIntersectedLocationGroupIds: groupFilter?.intersectedIds,
      iMetricCodes: metrics,
    },
    skip:
      !currentPeriod ||
      !priorPeriod ||
      !(variables.allStores?.locationGroupIds || groupFilter?.ids),
  })

  return {
    data: useMemo(() => {
      if (!data) return null

      return [
        ...metrics,
        ...metrics.map((m) => `prior_${m}`),
        ...metrics.map((m) => `yoy_${m}`),
      ].reduce((result, metricCode) => {
        const type = (() => {
          if (/^yoy_/.test(metricCode)) return 'yoy' as const
          if (/^prior_/.test(metricCode)) return 'prior' as const
          return 'current' as const
        })()
        const dataKey =
          type === 'current'
            ? ('listLocationGroupMetricValues' as const)
            : (`${type}ListLocationGroupMetricValues` as const)
        const key = metricCode.replace(/^(yoy|prior)_/, '')
        const d =
          data?.[dataKey]?.nodes.find((n) => n.metricSummaryData)
            ?.metricSummaryData?.[key] || null
        const unit = d?.unit || null
        let value = d?.value || 0

        if (value) {
          if (unit === 'CENT') {
            value *= 0.01
          } else if (unit === 'PERCENTAGE') {
            value *= 100
          }
        }

        return {
          ...result,
          [metricCode]: value,
        }
      }, {} as Record<string, number>)
    }, [data, metrics]),
    loading,
  }
}

export default useFetchGroupMetricValue
